/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/cancelSubscription.scss';

import React, {FocusEventHandler, MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {ChangeFunction, CloseButton, ErrorType, Loader, WithBackground} from '@dvrd/dvr-controls';

import {CancelType} from '../../../../util/interfaces';

import {BluemateButton, BluemateInput} from '../../../controls';

interface Props {
    onSubmit: MouseEventHandler;
    onCancel: MouseEventHandler;
    onChange: ChangeFunction<string>;
    onResetError: FocusEventHandler;
    reason: string;
    active: boolean;
    loading: boolean;
    type: CancelType;
    error: ErrorType;
}

export default function CancelSubscription(props: Props) {
    const {t} = useTranslation();
    const {reason, onCancel, onSubmit, active, loading, type, onChange, error, onResetError} = props;
    const [title, btnText, action] = useMemo(() => {
        if (type === CancelType.SUBSCRIPTION)
            return [
                t('settings.subscription.cancelSubscriptionTitle', 'Abonnement stoppen'),
                t('settings.subscription.cancelSubscriptionBtn', 'Ik begrijp het, abonnement stoppen'),
                t('settings.subscription.cancelSubscriptionAction', 'abonnement wilt stoppen')
            ];
        return [
            t('settings.subscription.deleteAccountTitle', 'Account verwijderen'),
            t('settings.subscription.deleteAccountBtn', 'Ik begrijp het, account verwijderen'),
            t('settings.subscription.deleteAccountAction', 'account wilt verwijderen')
        ];
    }, [type, t]);

    return (
        <WithBackground active={active} onClose={onCancel}>
            <div className='cancel-subscription default-popup btn-2'>
                <div className='header-container'>
                    <label className='header-title'>{title}</label>
                    <CloseButton onClick={onCancel} />
                </div>
                <div className='body-container'>
                    <Loader active={loading} />
                    <p className='message'>
                        {t('settings.subscription.cancelMessage1', 'We vinden het jammer dat je je {{action}}.', {
                            action
                        })}
                        <br />
                        <br />
                        {t(
                            'settings.subscription.cancelMessage2',
                            'Als je op de knop hieronder klikt, stopt het abonnement direct.'
                        )}
                        <br />
                        {t('settings.subscription.attention', 'Let op,')}
                        <br />
                        {t(
                            'settings.subscription.cancelMessage3',
                            'Je betaalt alleen nog voor het aantal orders dat we al hebben verwerkt tot nu.'
                        )}
                        <br />
                        {t('settings.subscription.cancelMessage4Pre', 'Je kunt dus nog')}{' '}
                        <b>{t('settings.subscription.cancelMessage4Bold', '1 laatste incasso')}</b>{' '}
                        {t('settings.subscription.cancelMessage4Post', 'verwachten voor de afgelopen maand.')}
                        <br />
                        <br />
                        {t(
                            'settings.subscription.cancelMessage5',
                            'Graag hieronder met ons delen waarom je stopt. Het helpt ons altijd :)'
                        )}
                        <br />
                        {t('settings.subscription.cancelSignature', 'Groet Dave en Levi.')}
                    </p>
                    <BluemateInput
                        onChange={onChange}
                        value={reason}
                        onFocus={onResetError}
                        error={error}
                        inputProps={{
                            placeholder: t(
                                'settings.subscription.feedbackPlaceholder',
                                'Feedback hier altijd welkom :)'
                            ),
                            rows: 5
                        }}
                        area
                    />
                </div>
                <div className='footer-container'>
                    <BluemateButton
                        onClick={onCancel}
                        label={t('settings.subscription.cancel', 'Annuleren')}
                        disabled={loading}
                    />
                    <BluemateButton onClick={onSubmit} label={btnText} disabled={loading} secondary color={'red'} />
                </div>
            </div>
        </WithBackground>
    );
}
